import { useEffect, useState } from 'react';

import { Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useAppDispatch } from '@/store/hooks';
import { patientsApiSlice } from '@/store/slices/api';
import { useUpdateEriTrackingMutation } from '@/store/slices/api/updateEriTrackingApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import CustomModal from '@/ui/Modal';

import { EriActionDevice, eriDeviceLabel } from './eriTrackingActions';

interface EriTrackingActionModalProps {
  opened: boolean;
  mode: 'start' | 'stop';
  devices: EriActionDevice[];
  patientId: string;
  onClose: () => void;
}

export function EriTrackingActionModal({
  opened,
  mode,
  devices,
  patientId,
  onClose
}: EriTrackingActionModalProps) {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [updateEriTracking, { isLoading }] = useUpdateEriTrackingMutation();
  const dispatch = useAppDispatch();
  const starting = mode === 'start';

  useEffect(() => {
    if (!opened) return;
    setDeviceId(devices.length === 1 ? (devices[0].id ?? null) : null);
  }, [opened, devices]);

  const handleSubmit = async () => {
    if (!deviceId) return;
    try {
      await updateEriTracking({ deviceId, isEriTracked: starting }).unwrap();
      dispatch(
        patientsApiSlice.util.invalidateTags([
          { type: API_SLICE.Patient, id: patientId },
          { type: API_SLICE.Patient, id: `${patientId}-schedules` }
        ])
      );
      notifications.show({
        title: starting ? 'ERI tracking started' : 'ERI tracking stopped',
        message: starting
          ? 'ERI tracking is on for that device. The scheduler updated its dates.'
          : 'ERI tracking is off for that device.',
        color: 'teal'
      });
      onClose();
    } catch {
      notifications.show({
        title: 'ERI tracking was not updated',
        message: 'The device was left unchanged.',
        color: 'red'
      });
    }
  };

  return (
    <CustomModal
      header={{ title: starting ? 'Start ERI Tracking' : 'Stop ERI Tracking' }}
      modalProps={{ opened, onClose, size: 'md' }}
      secondaryButton={{ text: 'Cancel', onClick: onClose }}
      primaryButton={{
        text: starting ? 'Start ERI Tracking' : 'Stop ERI Tracking',
        onClick: handleSubmit,
        isDisabled: !deviceId,
        isLoading
      }}>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">
          {starting
            ? 'Sets ERI tracking on the device. The scheduler updates that device’s dates.'
            : 'Removes ERI tracking from the device.'}
        </Text>
        {devices.length > 1 ? (
          <Select
            label="Device"
            placeholder="Select a device"
            data={devices.map((device) => ({
              value: device.id ?? '',
              label: eriDeviceLabel(device)
            }))}
            value={deviceId}
            onChange={setDeviceId}
          />
        ) : (
          <Text size="sm">{devices[0] ? eriDeviceLabel(devices[0]) : ''}</Text>
        )}
      </Stack>
    </CustomModal>
  );
}
